import * as TypographyStyles from '../TypographyStyles';
import styled from "styled-components";
import { Moon, Bus } from 'lucide-react';
import levelHigh from '../../icons/levelHigh.svg'
import levelMedium from '../../icons/levelMedium.svg'
import levelLow from '../../icons/levelLow.svg'
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { StyledTooltip } from '../TooltipStyles';

/** Night train line name prefixes (matches iOS isNightTrain). */
const NIGHT_TRAIN_PREFIXES = ['es ', 'nj', 'en', 'icn', 'intercites de nuit'];

function isNightTrain(lineName: string): boolean {
  const lower = lineName.toLowerCase();
  return NIGHT_TRAIN_PREFIXES.some(prefix => lower.startsWith(prefix));
}

function isBus(lineName: string): boolean {
  return lineName.toLowerCase().includes('bus');
}

/** Eurostar line names only — not Eurocity / European Sleeper. */
function isEurostarLineName(lineName: string): boolean {
  const lower = lineName.toLowerCase().trim();
  // "Eurostar …", or short codes "EUR 1234" / "EUR1234" / "EST 1234" / "EST1234"
  return (
    lower.startsWith('eurostar') ||
    /^eur[\s-]?\d/.test(lower) ||
    /^est[\s-]?\d/.test(lower)
  );
}

/** Normalizes line name for operator logo matching (matches iOS SNCF branch). */
function normalizeLineNameForOperatorLogo(lineName: string): string {
  return lineName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/** Île-de-France RER line → public asset basename (matches iOS sncfRerLineGlyphs). */
const SNCF_RER_LINE_LOGOS: Record<string, string> = {
  'rer a': 'rer-a',
  'rer b': 'rer-b',
  'rer c': 'rer-c',
  'rer d': 'rer-d',
  'rer e': 'rer-e',
};

// TODO slightly adjusted version of splitRoute already in the codebase in database/
function splitRoute(route: string) {
  const routeTrimmed = route.trim();
  const parts = routeTrimmed.split(/[ _-]/);
  if (parts.length === 1) {
    const m = routeTrimmed.match(/(.*[^0-9]|)([0-9]+)/);
    if (m) {
      return [m[1], m[2]];
    } else {
      return [routeTrimmed, ''];
    }
  } else {
    const firstPart = parts.slice(0, -1).join(' ');
    const secondPart = parts[parts.length - 1];
    return [firstPart, secondPart];
  }
}


type LineBadgeProps = {
  lineName: string;
  occupancy?: string;
  operatorId?: string;
  operatorName?: string;
}


const OperatorLogo = styled('img')`
  height: 12px;
  width: auto;
  flex-shrink: 0;
  object-fit: contain;
  object-position: left center;
`

const OccupancyIcon = styled('img')`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const Container = styled('div')`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background: ${slateColors[50]};
  border: 1px solid ${slateColors[300]};
  border-radius: 0.75rem;
  gap: 0.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    border-color: ${slateColors[400]};
  }
`

// Returns the operator logo path based on line name and operator ID
// Priority: line name prefixes first, then operator ID
const getOperatorLogoPath = (lineName?: string, operatorId?: string): string | null => {
  // Special case: ceske-drahy-db-fernverkehr-ag or db-fernverkehr-ag-ceske-drahy-mav with RJ/RJX prefix should show CD logo
  if (lineName && operatorId) {
    const lowercasedLineName = lineName.toLowerCase();
    const lowercasedOperatorId = operatorId.toLowerCase();
    
    if ((lowercasedOperatorId === "ceske-drahy-db-fernverkehr-ag" ||
         lowercasedOperatorId === "db-fernverkehr-ag-ceske-drahy-mav") &&
        (lowercasedLineName.startsWith("rj") || lowercasedLineName.startsWith("rjx"))) {
      return "/images/Operator logos/CD logo.imageset/CD logo.svg";
    }
  }
  
  // Priority: Check line name prefixes first (before operator ID)
  if (lineName) {
    const lowercasedLineName = lineName.toLowerCase();

    // SNCF-specific wordmarks (same order as iOS getOperatorLogoName)
    if (operatorId?.toLowerCase() === 'sncf') {
      const normalizedLine = normalizeLineNameForOperatorLogo(lineName);
      const rerBasename = SNCF_RER_LINE_LOGOS[normalizedLine];
      if (rerBasename) {
        return `/images/Operator logos/${rerBasename}.imageset/${rerBasename}.png`;
      }
      if (normalizedLine.startsWith('tgv inoui')) {
        return '/images/Operator logos/Logo_TGV_inOui_2017.imageset/Logo_TGV_inOui_2017.png';
      }
      if (normalizedLine.startsWith('tgv lyria')) {
        return '/images/Operator logos/TGV_Lyria_-_logo_officiel_2017.imageset/TGV_Lyria_-_logo_officiel_2017.png';
      }
    }

    const normalizedForOuigo = normalizeLineNameForOperatorLogo(lineName);
    if (
      normalizedForOuigo.startsWith('tgv ouigo') ||
      lowercasedLineName.startsWith('ogv') ||
      lowercasedLineName.startsWith('otc') ||
      lowercasedLineName.startsWith('ouigo')
    ) {
      return '/images/Operator logos/ouigo-wordmark.imageset/ouigo-wordmark.svg';
    }
    
    // Check line name prefixes in order
    if (lowercasedLineName.startsWith("tgv")) {
      return "/images/Operator logos/Sncf-logo.imageset/Sncf-logo.png";
    }
    if (lowercasedLineName.startsWith("ice")) {
      return "/images/Operator logos/Deutsche_Bahn_AG-Logo.imageset/Deutsche_Bahn_AG-Logo.svg";
    }
    if (isEurostarLineName(lineName)) {
      return "/images/Operator logos/eurostar.imageset/eurostar.svg";
    }
    if (lowercasedLineName.startsWith("nj")) {
      return "/images/Operator logos/Nightjet_logo.imageset/Nightjet_logo.svg";
    }
    if (lowercasedLineName.startsWith("rj")) {
      return "/images/Operator logos/Logo_OEBB.imageset/Logo_OEBB.svg";
    }
    if (lowercasedLineName.startsWith("ecd")) {
      return "/images/Operator logos/Nederlandse_Spoorwegen_logo.imageset/Nederlandse_Spoorwegen_logo.svg";
    }
    if (lowercasedLineName.startsWith("fr")) {
      return "/images/Operator logos/Trenitalia_logo_RDT.imageset/Trenitalia_logo_RDT.svg";
    }
  }
  
  // Fallback: If we have operator ID, use it for matching
  if (!operatorId) return null;
  
  const id = operatorId.toLowerCase();
  
  // Check for DB Regio variants
  if (id.includes("db-regio")) {
    return "/images/Operator logos/Deutsche_Bahn_AG-Logo.imageset/Deutsche_Bahn_AG-Logo.svg";
  }
  
  // Check for Arriva variants
  if (id.includes("arriva")) {
    return "/images/Operator logos/Arriva.imageset/Arriva.svg";
  }
  
  // Check for S-Bahn variants
  if (id.includes("s-bahn")) {
    return "/images/Operator logos/S-Bahn-Logo.imageset/S-Bahn-Logo.svg";
  }
  
  switch (id) {
    case "db-fernverkehr-ag":
    case "db-fernverkehr-ag-danische-staatsbahnen":
    case "nederlandse-spoorwegen-db-fernverkehr-ag":
    case "db-fernverkehr-ag-nederlandse-spoorwegen":
      return "/images/Operator logos/Deutsche_Bahn_AG-Logo.imageset/Deutsche_Bahn_AG-Logo.svg";
    case "pkp-intercity":
    case "pkp-intercity-db-fernverkehr-ag":
    case "db-fernverkehr-ag-pkp-intercity":
      return "/images/Operator logos/Logo_pkp_ic.imageset/Logo_pkp_ic.png";
    case "sncb":
      return "/images/Operator logos/SNCB_logo.imageset/SNCB_logo.svg";
    case "sncf":
      return "/images/Operator logos/Sncf-logo.imageset/Sncf-logo.png";
    case "nederlandse-spoorwegen":
      return "/images/Operator logos/Nederlandse_Spoorwegen_logo.imageset/Nederlandse_Spoorwegen_logo.svg";
    case "flixtrain":
      return "/images/Operator logos/FlixTrain_Logo_2020.imageset/FlixTrain_Logo_2020.svg";
    case "osterreichische-bundesbahnen":
    case "osterreichische-bundesbahnen-trenitalia":
    case "osterreichische-bundesbahnen-ceske-drahy":
    // ÖBB's own hafas-client profile (as opposed to DB's vendo profile) labels
    // its trains with this generic operator id instead of "osterreichische-bundesbahnen".
    case "nahreisezug":
      return "/images/Operator logos/Logo_OEBB.imageset/Logo_OEBB.svg";
    case "eurostar":
      return "/images/Operator logos/eurostar.imageset/eurostar.svg";
    case "european-sleeper":
      return "/images/Operator logos/European_Sleeper_logo.imageset/European_Sleeper_logo.svg";
    case "trenitalia":
    case "trenitalia-schweizerische-bundesbahnen":
    case "trenitalia-osterreichische-bundesbahnen":
      return "/images/Operator logos/Trenitalia_logo_RDT.imageset/Trenitalia_logo_RDT.svg";
    case "schweizerische-bundesbahnen":
    case "schweizerische-bundesbahnen-trenitalia":
      return "/images/Operator logos/SBB_CFF_FFS_logo.imageset/SBB_CFF_FFS_logo 2.svg";
    case "westbahn":
    // ÖBB's own hafas-client profile labels Westbahn with its full legal name.
    case "westbahn-management-gmbh":
      return "/images/Operator logos/Westbahn_Logo.imageset/Westbahn_Logo.svg";
    case "danische-staatsbahnen":
    case "danische-staatsbahnen-db-fernverkehr-ag":
      return "/images/Operator logos/DSB_company_logo.imageset/DSB_company_logo.svg";
    case "ceske-drahy":
    case "ceske-drahy-osterreichische-bundesbahnen":
      return "/images/Operator logos/CD logo.imageset/CD logo.svg";
    case "sj":
      return "/images/Operator logos/SJ_(rail_operator)_logo.imageset/SJ_(rail_operator)_logo.svg";
    case "skanetrafiken-oresundstag":
      return "/images/Operator logos/Oresundstag_logo.imageset/Oresundstag_logo.svg";
    case "renfe":
      return "/images/Operator logos/Logotipo_de_Renfe_Operadora.imageset/Logotipo_de_Renfe_Operadora 1.svg";
    case "ouigo-espana":
      return "/images/Operator logos/ouigo-wordmark.imageset/ouigo-wordmark.svg";
    case "iryo":
      return "/images/Operator logos/Iryo_2021_logo.imageset/Iryo_2021_logo.svg";
    case "regiojet":
      return "/images/Operator logos/regiojet.imageset/regiojet.svg";
    case "cfl":
    case "cfl-sncb":
    case "cfl-sncf":
    case "sncb-cfl":
      return "/images/Operator logos/Logo_CFL_Luxembourg_(2022).imageset/Logo_CFL_Luxembourg_(2022).svg";
    case "leo-express":
      return "/images/Operator logos/Leo Express.imageset/Leo Express.svg";
    case "ostdeutsche-eisenbahn-gmbh":
      return "/images/Operator logos/ODEG-Logo_Neu.imageset/ODEG-Logo_Neu.svg";
    case "trenord":
      return "/images/Operator logos/trenord-logo.imageset/trenord-logo.svg";
    case "mav":
      return "/images/Operator logos/MAV-logo.imageset/MAV-logo.svg";
    case "dsb-s-tog":
      return "/images/Operator logos/S-tog logo.imageset/S-tog logo.png";
    case "movia-lokalbanen":
      return "/images/Operator logos/movia logo.imageset/movia logo.png";
    case "gocollective":
      return "/images/Operator logos/gocollective logo.imageset/gocollective logo.png";
    case "midttrafik":
      return "/images/Operator logos/midttrafik symbol.imageset/midttrafik symbol.png";
    case "nordjyske-jernbaner":
      return "/images/Operator logos/Nordjyske Jernbaner logo.imageset/Nordjyske Jernbaner logo.png";
    case "govolta":
      return "/images/Operator logos/gv_logo.imageset/gv_logo.png";
    case "nomad":
      return "/images/Operator logos/NOMAD logo.imageset/NOMAD logo.png";
    case "fluo":
      return "/images/Operator logos/fluo logo.imageset/fluo logo.png";
    case "trsi-transdev-rail-sud-inter-metropoles":
      return "/images/Operator logos/Zou-PACA logo.imageset/Zou-PACA logo.png";
    default:
      return null;
  }
};

const LineBadge = ({ lineName, occupancy, operatorId, operatorName }: LineBadgeProps) => {
  const [routeType, routeNumber] = lineName ? splitRoute(lineName) : ['', ''];
  const getOccupancyIcon = () => {
    if (occupancy === 'low') return levelLow;
    else if (occupancy === 'low-to-medium') return levelMedium;
    else if (occupancy === 'high' || occupancy === 'very-high' || occupancy === 'exceptionally-high') return levelHigh;
    return '';
  }

  const operatorLogoPath = getOperatorLogoPath(lineName, operatorId);

  return (
    <>
      <Container data-tooltip-id="operator-logo-tooltip" data-tooltip-content={operatorName}>
        {operatorLogoPath && <OperatorLogo src={operatorLogoPath} alt="" />}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <TypographyStyles.BodyLBold color={slateColors[900]}>{routeType}</TypographyStyles.BodyLBold>
          {routeNumber && (
            <TypographyStyles.BodyLRegular color={slateColors[600]}>{routeNumber}</TypographyStyles.BodyLRegular>
          )}
          {lineName && isBus(lineName) && (
            <Bus size={14} color={slateColors[600]} style={{ flexShrink: 0 }} aria-hidden />
          )}
          {lineName && isNightTrain(lineName) && (
            <Moon size={14} color={slateColors[600]} style={{ flexShrink: 0 }} aria-hidden />
          )}
        </div>
        {occupancy && <OccupancyIcon alt="occupancy" src={getOccupancyIcon()} />}
      </Container>
      <StyledTooltip id="operator-logo-tooltip" place="top" className="tooltip-styled" />
    </>
  );
}

export default LineBadge;
